import type { CmmdBoardTask, CmmdMember, CmmdProjectAgentMember } from "@cmmd-forge/contracts";
import type { ScopedProjectRef, ScopedThreadRef } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { buildTaskThreadIndex } from "~/lib/taskThreadLink";
import type { DataViewRouteState } from "~/views/viewController";
import type { DataViewState } from "~/views/viewDefinition";
import type { ProjectTaskSort } from "~/views/viewDefinition";
import type { ProjectTaskBoard } from "./useProjectTaskBoard";
import { ProjectTaskSurfaceBody } from "./ProjectTaskSurfaceBody";
import type { ProjectTaskWorkbenchDataProps } from "./ProjectTaskDetail";
import { ProjectTaskToolbar } from "./ProjectTaskToolbar";
import { useProjectTaskChrome, type ProjectTaskChrome } from "./useProjectTaskChrome";
import { filterProjectTasks } from "./taskViewFilters";
import { flattenTaskTree, normalizeTaskStatus, type ProjectTaskViewProps } from "./taskViewTypes";
import { ProjectTaskSurfaceDialogs } from "./ProjectTaskSurfaceDialogs";
import { projectTaskSurfaceStatus } from "./projectTaskSurfaceStatus";
import { isProjectTaskBacklogView } from "./projectTaskBacklogView";

export interface ProjectTaskSurfaceProps {
  readonly projectId: number;
  readonly projectRef?: ScopedProjectRef | null;
  readonly routeViewState: DataViewRouteState;
  readonly onViewStateChange: (
    state: DataViewState,
    savedViewId: string | null,
    previousState: DataViewState,
    previousSavedViewId: string | null,
  ) => void;
  readonly savedViewsEnabled?: boolean;
  readonly board: ProjectTaskBoard;
  readonly projectTitle?: string;
  readonly projectIconUrl?: string | null;
  readonly projectIcon?: string | null;
  /** Project work-item prefix for list ID display (`CDT-261`). */
  readonly workItemPrefix?: string | null;
  readonly members: readonly CmmdMember[];
  readonly agentMembers?: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading?: boolean;
  readonly agentMembersError?: string | null;
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly startingTaskId: number | null;
  readonly conversationTaskIds?: ReadonlySet<number>;
  readonly onOpenThread: (taskId: number) => void;
  readonly onOpenThreadSummary?: ((thread: SidebarThreadSummary) => void) | undefined;
  readonly onStartWork: (task: CmmdBoardTask) => Promise<boolean>;
  readonly selection: ProjectTaskSelection;
  readonly onSelectionChange: (selection: ProjectTaskSelection) => void;
  /** Set when a CMMD handoff link's `task` id does not match a task on this board. */
  readonly invalidTaskNotice?: string | null;
  readonly workbenchData?: ProjectTaskWorkbenchDataProps;
  /**
   * Shared chrome from project home. When set, this surface does not own the
   * data-view controller / composer (home renders the toolbar in the tabs row).
   */
  readonly chrome?: ProjectTaskChrome;
  /** Default `inline`. Use `external` when the toolbar is rendered in project chrome. */
  readonly toolbarPlacement?: "inline" | "external";
  readonly onProjectLeadThreadReady?: ((threadRef: ScopedThreadRef) => void) | undefined;
}

export type ProjectTaskSelection =
  | { readonly kind: "none" }
  | { readonly kind: "task"; readonly taskId: number }
  | { readonly kind: "projectLead" };

function SurfaceToolbar(props: {
  readonly placement: "inline" | "external";
  readonly board: ProjectTaskBoard;
  readonly chrome: ProjectTaskChrome;
  readonly projectId: number;
  readonly savedViewsEnabled: boolean;
}) {
  const toolbar = (
    <ProjectTaskToolbar
      viewController={props.chrome.viewController}
      composerOpen={props.chrome.composerOpen}
      onToggleComposer={props.chrome.toggleComposer}
      savedViewsEnabled={props.savedViewsEnabled}
      tasks={props.board.tasks}
      milestones={props.chrome.milestones.milestones}
      onManageMilestones={() => props.chrome.setMilestoneManagerOpen(true)}
      variant="bar"
      projectId={props.projectId}
      onWorkflowSaved={props.board.reload}
      workflowAvailable={props.board.workflow != null}
      taskMutationsDisabled={props.board.workflowMutationBlocked}
    />
  );
  return props.placement === "inline" ? toolbar : <div className="md:hidden">{toolbar}</div>;
}

/**
 * The project's Tasks tab: List/Board view switch, title search, inline task
 * creation, and the live task→thread index. Receives its data via the board
 * hook and members from the parent (no double fetch).
 */
export function ProjectTaskSurface({
  projectId,
  projectRef = null,
  routeViewState,
  onViewStateChange,
  savedViewsEnabled = true,
  board,
  projectTitle = "Project",
  projectIconUrl = null,
  projectIcon = null,
  workItemPrefix = null,
  members,
  agentMembers = EMPTY_AGENT_MEMBERS,
  agentMembersLoading = false,
  agentMembersError = null,
  threadSummaries,
  startingTaskId,
  conversationTaskIds = EMPTY_TASK_ID_SET,
  onOpenThread,
  onOpenThreadSummary,
  onStartWork,
  selection,
  onSelectionChange,
  invalidTaskNotice = null,
  workbenchData,
  chrome: chromeProp,
  toolbarPlacement = "inline",
  onProjectLeadThreadReady,
}: ProjectTaskSurfaceProps) {
  const fallbackChrome = useProjectTaskChrome({
    projectId,
    routeViewState,
    onViewStateChange,
    savedViewsEnabled,
    enabled: chromeProp == null,
  });
  const chrome = chromeProp ?? fallbackChrome;
  const viewController = chrome.viewController;
  const view = viewController.state.layout;

  const threadIndex = buildTaskThreadIndex(threadSummaries);
  const allTasks = flattenTaskTree(board.tasks);
  const openTask =
    selection.kind === "task"
      ? (allTasks.find((task) => task.id === selection.taskId) ?? null)
      : null;

  const searchableTasks = viewController.state.search.trim().length > 0 ? allTasks : board.tasks;
  const visibleTasks = filterProjectTasks(searchableTasks, viewController.state, workItemPrefix);

  const surfaceMutationError = chrome.composerOpen ? null : board.mutationError;
  const milestoneGrouped = view !== "timeline" && viewController.state.taskGroupBy === "milestone";
  const surfaceStatus = projectTaskSurfaceStatus({
    boardLoading: board.loading,
    boardError: board.error,
    milestoneGrouped,
    milestonesLoading: chrome.milestones.loading,
    milestonesError: chrome.milestones.loadError ?? null,
  });

  const openTaskDetail = (taskId: number) => {
    onSelectionChange({ kind: "task", taskId });
  };

  const closeTaskDetail = () => {
    onSelectionChange({ kind: "none" });
  };

  const archiveTaskFromDetail = async (taskId: number) => {
    const archived = await board.archive(taskId);
    if (archived && selection.kind === "task" && selection.taskId === taskId) {
      onSelectionChange({ kind: "none" });
    }
    return archived;
  };

  const viewProps: ProjectTaskViewProps = {
    tasks: visibleTasks,
    backlogView: isProjectTaskBacklogView(viewController.state),
    threadIndex,
    pendingTaskIds: board.pendingTaskIds,
    conversationTaskIds,
    onOpenTask: openTaskDetail,
    onSetStatus: board.setStatus,
    onOpenThread,
    workItemPrefix,
    onCreateInGroup: (statusKey: string) => {
      const stage = board.workflow?.stages.find((candidate) => candidate.id === statusKey);
      chrome.openComposerFor({
        status: stage?.category ?? normalizeTaskStatus(statusKey),
        milestoneId: null,
        ...(stage != null ? { workflowStageId: stage.id } : {}),
      });
    },
    onCreateInLane: chrome.openComposerFor,
    onManageMilestone: chrome.openMilestoneManager,
    groupBy: viewController.state.taskGroupBy,
    milestones: chrome.milestones.milestones,
    taskSort: viewController.state.taskSort ?? null,
    onTaskSortChange: (taskSort: ProjectTaskSort | null) => viewController.patchState({ taskSort }),
    workflow: board.workflow,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SurfaceToolbar
        placement={toolbarPlacement}
        board={board}
        chrome={chrome}
        projectId={projectId}
        savedViewsEnabled={savedViewsEnabled}
      />

      <ProjectTaskSurfaceDialogs
        chrome={chrome}
        board={board}
        projectId={projectId}
        projectRef={projectRef}
        projectTitle={projectTitle}
        tasks={allTasks}
        openTask={openTask}
        members={members}
        agentMembers={agentMembers}
        agentMembersLoading={agentMembersLoading}
        agentMembersError={agentMembersError}
        projectIconUrl={projectIconUrl}
        projectIcon={projectIcon}
        threadSummaries={threadSummaries}
        startingTaskId={startingTaskId}
        conversationTaskIds={conversationTaskIds}
        onCloseTask={closeTaskDetail}
        onOpenTask={openTaskDetail}
        onStartWork={onStartWork}
        onOpenThread={onOpenThreadSummary ?? ignoreThreadSummaryOpen}
        onArchiveTask={archiveTaskFromDetail}
        onProjectLeadThreadReady={onProjectLeadThreadReady}
        {...(workbenchData ? { workbenchData } : {})}
      />

      {invalidTaskNotice ? (
        <p className="border-b border-warning-border bg-warning-surface px-4 py-1.5 text-xs text-warning-foreground">
          {invalidTaskNotice}
        </p>
      ) : null}

      {surfaceMutationError ? (
        <p className="border-b border-destructive-border bg-destructive-surface px-4 py-1.5 text-xs text-destructive">
          {surfaceMutationError}
        </p>
      ) : null}

      {board.workflowMutationBlocked ? (
        <p className="border-b border-warning-border bg-warning-surface px-4 py-1.5 text-xs text-warning-foreground">
          Task workflow is unavailable. Status changes, task creation, and board moves are
          read-only.
        </p>
      ) : null}

      <ProjectTaskSurfaceBody
        view={view}
        loading={surfaceStatus.loading}
        error={surfaceStatus.error}
        viewProps={viewProps}
        onMoveTask={board.moveTask}
        reorderingDisabled={viewController.state.search.trim().length > 0}
        mutationDisabled={board.workflowMutationBlocked}
        onRetry={() => {
          void board.reload();
          if (milestoneGrouped) void chrome.milestones.refresh();
        }}
      />
    </div>
  );
}

const EMPTY_TASK_ID_SET: ReadonlySet<number> = new Set<number>();
const EMPTY_AGENT_MEMBERS: readonly CmmdProjectAgentMember[] = [];

function ignoreThreadSummaryOpen(_thread: SidebarThreadSummary): void {
  // Story/test surfaces may omit exact thread navigation. Production supplies it.
}
